import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  verifyDeleteAccountPassword,
  deleteAccount,
} from '../api/authApi';
import { api } from '../config';
import Avatar from '../components/Avatar';
import './AccountInfoPage.css';

function isTokenExpired(token) {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    if (!payload.exp) return true;
    return Date.now() >= payload.exp * 1000;
  } catch {
    return true;
  }
}
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_LENGTH = 64;
const AccountInfoPage = () => {
  const { accessToken, clearAuthSession } = useAuth();
    const [formData, setFormData] = useState({
        profile_picture: '',
        name: '',
        email: '',
        gender: '',
        dob: '',
        phone: '',
        currentPassword: '',  
        newPassword: '',
        confirmPassword: ''
      });
      
  const [profilePic, setProfilePic] = useState('');
  const [profilePicTimestamp, setProfilePicTimestamp] = useState(Date.now());
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteModalStep, setDeleteModalStep] = useState(null);
const [deletePassword, setDeletePassword] = useState('');
const [deletionToken, setDeletionToken] = useState('');
const [deleteError, setDeleteError] = useState('');
const [verifyingDeletePassword, setVerifyingDeletePassword] = useState(false);
const [deletingAccount, setDeletingAccount] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const newPasswordTooShort =
  formData.newPassword.length > 0 &&
  formData.newPassword.length < PASSWORD_MIN_LENGTH;

const newPasswordTooLong =
  formData.newPassword.length > PASSWORD_MAX_LENGTH;

const confirmPasswordMismatch =
  formData.confirmPassword.length > 0 &&
  formData.newPassword !== formData.confirmPassword;

const passwordValidationError =
  newPasswordTooShort || newPasswordTooLong || confirmPasswordMismatch;
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const res = await fetch(api.url('/api/users/me'), {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
  
        if (res.ok) {
          const data = await res.json();
          setFormData(prev => ({
            ...prev,
            profile_picture: data.profile_picture || '',
            name: data.name || '',
            email: data.email || '',
            gender: data.gender || '',
            dob: data.dob ? data.dob.split('T')[0] : '',
            phone: data.phone || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }));
        }
      } catch {
        // Intentionally silent in production
      }
    };
  
    if (accessToken) {
      fetchUserData();
    }
  }, [accessToken]);
  
  useEffect(() => {
    const fetchProfilePicture = async () => {
      try {
        const res = await fetch(api.url('/api/roam-share-posts/my-profile'), {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      if (res.ok) {
  const data = await res.json();
  if (data.profile_picture) {
    setProfilePic(data.profile_picture);
    setProfilePicTimestamp(Date.now());
  } else {
    setProfilePic('');
  }
  } else {
    setProfilePic('');
  }
  } catch {
    setProfilePic('');
  }
    };
  
    if (accessToken) {
      fetchProfilePicture();
    }
  }, [accessToken]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!showPasswordFields) return;
  
    if (formData.newPassword.length < PASSWORD_MIN_LENGTH) {
      alert('Password must contain at least 8 characters.');
      return;
    }
  
    if (formData.newPassword.length > PASSWORD_MAX_LENGTH) {
      alert('Password cannot exceed 64 characters.');
      return;
    }
  
    if (formData.newPassword !== formData.confirmPassword) {
      alert('Passwords do not match.');
      return;
    }
  
    const payload = {
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword,
    };
  
    try {
      const res = await fetch(api.url('/api/users/change-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
  
      const data = await res.json();
  
      if (res.ok) {
        alert('✅ Password updated successfully!');
        setFormData(prev => ({
          ...prev,
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        }));
        setShowPasswordFields(false);
      } else {
        alert(`❌ Password update failed: ${data.error || 'Unknown error'}`);
      }
    } catch {
      alert('❌ Password update failed: Internal server error, please try again later');
    }
  };
  const openDeleteAccountModal = () => {
    setDeleteModalStep('password');
    setDeletePassword('');
    setDeletionToken('');
    setDeleteError('');
  };
  
  const closeDeleteAccountModal = () => {
    if (verifyingDeletePassword || deletingAccount) {
      return;
    }
  
    setDeleteModalStep(null);
    setDeletePassword('');
    setDeletionToken('');
    setDeleteError('');
  };
  const handleVerifyDeletePassword = async (e) => {
    e.preventDefault();
  
    if (!deletePassword.trim()) {
      setDeleteError('Enter your current password.');
      return;
    }
  
    setVerifyingDeletePassword(true);
    setDeleteError('');
  
    try {
      const data = await verifyDeleteAccountPassword(
        deletePassword,
        accessToken
      );
  
      if (!data?.deletionToken) {
        throw new Error(
          'The server did not return a deletion confirmation token.'
        );
      }
  
      setDeletionToken(data.deletionToken);
      setDeletePassword('');
      setDeleteModalStep('confirm');
    } catch (error) {
      if (error.status === 401) {
        setDeleteError(
          error.message || 'Current password is incorrect.'
        );
      } else if (error.status === 403) {
        setDeleteError(
          'Your session is no longer valid. Please log in again.'
        );
      } else {
        setDeleteError(
          error.message ||
            'Unable to verify your password. Please try again.'
        );
      }
    } finally {
      setVerifyingDeletePassword(false);
    }
  };
  const handlePermanentDelete = async () => {
    if (!deletionToken) {
      setDeleteError(
        'Your deletion confirmation is missing. Please enter your password again.'
      );
      setDeleteModalStep('password');
      return;
    }
  
    setDeletingAccount(true);
    setDeleteError('');
  
    try {
      await deleteAccount(deletionToken, accessToken);
  
      clearAuthSession();
  
      navigate('/login', {
        replace: true,
        state: {
          accountDeleted: true,
        },
      });
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        setDeletionToken('');
        setDeleteModalStep('password');
        setDeleteError(
          'Your deletion confirmation expired or is invalid. Please enter your password again.'
        );
      } else {
        setDeleteError(
          error.message ||
            'Account deletion failed. Your account has not been deleted.'
        );
      }
    } finally {
      setDeletingAccount(false);
    }
  };
  useEffect(() => {
    if (!accessToken || isTokenExpired(accessToken)) {
      navigate('/login', { state: { from: location.pathname } });
    }
  }, [accessToken, navigate, location.pathname]);
  
  return (
    <div>
        <div className="header">
            <div className="brand" onClick={() => navigate("/")}>
            <img src="/images/customroam-logo.png" alt="Logo" className="logo-img" />
            <span className="brand-text">CUSTOM ROAM</span>
             </div>
              {/* Hamburger menu button */}
              <button className="header-menu-btn" onClick={() => setMenuOpen(!menuOpen)}>
                ☰
               </button>
             <div className={`nav-links ${menuOpen ? 'open' : ''}`}>
                    <span className="nav-link" onClick={() => navigate('/')}>Home</span>
                    <span className="nav-link" onClick={() => navigate('/roam-share')}>Roam Share</span>
                    <span className="nav-link" onClick={() => navigate('/my-itineraries')}>My Itineraries</span>
                    <span className="notification-icon" onClick={() => navigate('/notifications')}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                       height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                     className="bell-svg"
                    >
                        <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"></path>
                         <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                    </svg>
                 </span>
                    <span className="nav-link" onClick={() => navigate('/profile')}>Profile</span>
                </div>
            </div>

    <div className="account-info-container">
      <h2>Account Information</h2>
      <div className="profile-picture-section">
      <Avatar
  src={
    profilePic
      ? `${profilePic}?t=${profilePicTimestamp}`
      : undefined
  }
  size={112}
  className="profile-picture"
/>
        {/* Optional: Add upload button */}
      </div>
      <div className="readonly-info">
       <p><strong>Full Name:</strong> {formData.name}</p>
       <p><strong>Email:</strong> {formData.email}</p>
       <p><strong>Gender:</strong> {formData.gender}</p>
       <p><strong>Date of Birth:</strong> {formData.dob}</p>
       <p><strong>Phone Number:</strong> {formData.phone}</p>
      </div>
      <button 
  className="change-password-toggle" 
  onClick={() => setShowPasswordFields(true)}
>
  Change Password
</button>

{showPasswordFields && (
  <>
    <h3>Change Password</h3>

    <div className="form-group">
      <label>Current Password</label>
      <input
        type="password"
        name="currentPassword"
        value={formData.currentPassword}
        onChange={handleChange}
        placeholder="Enter Current Password"
        maxLength={PASSWORD_MAX_LENGTH}
      />
    </div>

    <div className="form-group">
      <label>New Password</label>
      <input
        type="password"
        name="newPassword"
        value={formData.newPassword}
        onChange={handleChange}
        placeholder="Enter New Password"
        minLength={PASSWORD_MIN_LENGTH}
        maxLength={PASSWORD_MAX_LENGTH}
      />

      {newPasswordTooShort && (
        <p className="signup-error">
          Password must contain at least 8 characters.
        </p>
      )}

      {newPasswordTooLong && (
        <p className="signup-error">
          Password cannot exceed 64 characters.
        </p>
      )}
    </div>

    <div className="form-group">
      <label>Confirm New Password</label>
      <input
        type="password"
        name="confirmPassword"
        value={formData.confirmPassword}
        onChange={handleChange}
        placeholder="Re-enter New Password"
        minLength={PASSWORD_MIN_LENGTH}
        maxLength={PASSWORD_MAX_LENGTH}
      />

      {confirmPasswordMismatch && (
        <p className="signup-error">
          Passwords do not match.
        </p>
      )}
    </div>
  </>
)}
     {showPasswordFields && (
  <button
    className="save-button"
    onClick={handleSave}
    disabled={
      !formData.currentPassword ||
      !formData.newPassword ||
      !formData.confirmPassword ||
      passwordValidationError
    }
  >
    Save Changes
  </button>
)}
<section className="delete-account-section">
  <h3>Account Removal</h3>

  <p>
    Permanently delete your Custom Roam account and all associated
    content.
  </p>

  <button
    type="button"
    className="delete-account-button"
    onClick={openDeleteAccountModal}
  >
    Delete Account
  </button>
</section>
</div>

{deleteModalStep && (
  <div className="delete-modal-overlay">
    <div
      className="delete-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-account-title"
    >
      {deleteModalStep === 'password' && (
        <form onSubmit={handleVerifyDeletePassword}>
          <h2 id="delete-account-title">
            Verify Your Password
          </h2>

          <p className="delete-modal-description">
            Enter your current password to continue with permanent
            account deletion.
          </p>

          <div className="form-group">
            <label htmlFor="deletePassword">
              Current Password
            </label>

            <input
              id="deletePassword"
              type="password"
              value={deletePassword}
              onChange={(e) => {
                setDeletePassword(e.target.value);

                if (deleteError) {
                  setDeleteError('');
                }
              }}
              placeholder="Enter your current password"
              maxLength={PASSWORD_MAX_LENGTH}
              autoComplete="current-password"
              autoFocus
              disabled={verifyingDeletePassword}
            />
          </div>

          {deleteError && (
            <p className="delete-error" role="alert">
              {deleteError}
            </p>
          )}

          <div className="delete-modal-actions">
            <button
              type="button"
              className="delete-cancel-button"
              onClick={closeDeleteAccountModal}
              disabled={verifyingDeletePassword}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="delete-continue-button"
              disabled={
                verifyingDeletePassword || !deletePassword.trim()
              }
            >
              {verifyingDeletePassword
                ? 'Verifying...'
                : 'Continue'}
            </button>
          </div>
        </form>
      )}

      {deleteModalStep === 'confirm' && (
        <>
          <h2 id="delete-account-title">
            Permanently Delete Account?
          </h2>

          <div className="delete-warning-box">
            <p>This action cannot be undone.</p>

            <p>
              Your account and associated data will be permanently
              deleted, including:
            </p>

            <ul>
              <li>Saved itineraries</li>
              <li>Roam Share posts and media</li>
              <li>Bucket lists </li>
            </ul>
          </div>

          {deleteError && (
            <p className="delete-error" role="alert">
              {deleteError}
            </p>
          )}

          <div className="delete-modal-actions">
            <button
              type="button"
              className="delete-cancel-button"
              onClick={closeDeleteAccountModal}
              disabled={deletingAccount}
            >
              Keep My Account
            </button>

            <button
              type="button"
              className="delete-confirm-button"
              onClick={handlePermanentDelete}
              disabled={deletingAccount}
            >
              {deletingAccount
                ? 'Deleting Account...'
                : 'Permanently Delete'}
            </button>
          </div>
        </>
      )}
    </div>
  </div>
)}

</div>
);
};

export default AccountInfoPage;
